import React, { useState, useEffect, useCallback } from 'react'
import {
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
  RefreshCw,
  Search,
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Skeleton } from '../../components/ui/Skeleton'
import {
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/Select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet'
import {
  getApiLogs,
  getApiLogStats,
  getApiLogDetail,
  exportApiLogsCsv,
  type ApiLogEntry,
  type ApiLogDetail,
  type ApiLogStats,
  type ApiLogFilters,
} from '../../services/apiLogs'

// --------------- helpers ---------------

const METHOD_COLORS: Record<string, 'info' | 'success' | 'warning' | 'error' | 'default'> = {
  GET: 'info',
  POST: 'success',
  PUT: 'warning',
  PATCH: 'warning',
  DELETE: 'error',
}

const STATUS_LABELS: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  405: 'Method Not Allowed',
  409: 'Conflict',
  422: 'Validation Error',
  429: 'Rate Limited',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
}

function parseErrorDetail(raw: string): { message: string; details?: string[] } {
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed.detail === 'string') {
      return { message: parsed.detail }
    }
    if (Array.isArray(parsed.detail)) {
      const details = parsed.detail.map((d: { loc?: string[]; msg?: string; type?: string }) => {
        const field = d.loc ? d.loc.filter((s) => s !== 'body').join('.') : ''
        const typeHint = d.type ? ` (${d.type})` : ''
        return field ? `${field}: ${d.msg}${typeHint}` : `${d.msg || ''}${typeHint}`
      })
      return { message: 'Validation Error', details }
    }
    if (typeof parsed.detail === 'object' && parsed.detail !== null) {
      return { message: parsed.detail.message || parsed.detail.code || JSON.stringify(parsed.detail) }
    }
    if (typeof parsed.error === 'string') return { message: parsed.error }
    if (typeof parsed.message === 'string') return { message: parsed.message }
    return { message: raw }
  } catch {
    return { message: raw }
  }
}

function StatusBadge({ code }: { code: number }) {
  if (code < 300) return <Badge variant="success">{code}</Badge>
  if (code < 400) return <Badge variant="warning">{code}</Badge>
  return <Badge variant="error">{code}</Badge>
}

function formatTime(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(2)}s`
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts)
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function formatFullTimestamp(ts: string): string {
  return new Date(ts).toISOString()
}

// Recursively parse stringified JSON inside string values.
// API payloads often carry escaped JSON in fields like `content`, `metadata`, etc.
// Deep-parsing surfaces the real structure instead of a wall of \"escaped\" text.
function deepParseJson(value: unknown): unknown {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return deepParseJson(JSON.parse(trimmed))
      } catch {
        return value
      }
    }
    return value
  }
  if (Array.isArray(value)) {
    return value.map(deepParseJson)
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = deepParseJson(v)
    }
    return result
  }
  return value
}

// Decode literal \uXXXX escape sequences to real characters.
// Last-resort fallback for when JSON.parse fails on malformed input.
function unescapeUnicode(str: string): string {
  return str.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) =>
    String.fromCharCode(parseInt(hex, 16))
  )
}

function tryFormatJson(raw: string | null, deep: boolean = true): string {
  if (!raw) return ''
  try {
    const parsed = JSON.parse(raw)
    const final = deep ? deepParseJson(parsed) : parsed
    return JSON.stringify(final, null, 2)
  } catch {
    // Not valid JSON — at least decode unicode escapes so non-ASCII is readable.
    return unescapeUnicode(raw)
  }
}

// Lightweight JSON syntax highlighter — no external deps.
// Colorizes keys, strings, numbers, booleans, and null.
function highlightJson(json: string): React.ReactNode {
  if (!json) return null
  const tokenRegex = /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  while ((match = tokenRegex.exec(json)) !== null) {
    if (match.index > lastIndex) {
      parts.push(json.slice(lastIndex, match.index))
    }
    const [full, keyMatch, strMatch, boolNullMatch, numMatch] = match
    if (keyMatch) {
      parts.push(
        <span key={key++} className="text-sky-600 dark:text-sky-400">{full}</span>
      )
    } else if (strMatch) {
      parts.push(
        <span key={key++} className="text-emerald-600 dark:text-emerald-400">{full}</span>
      )
    } else if (boolNullMatch) {
      parts.push(
        <span key={key++} className="text-violet-600 dark:text-violet-400">{full}</span>
      )
    } else if (numMatch) {
      parts.push(
        <span key={key++} className="text-amber-600 dark:text-amber-500">{full}</span>
      )
    }
    lastIndex = match.index + full.length
  }
  if (lastIndex < json.length) {
    parts.push(json.slice(lastIndex))
  }
  return parts
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(2)} MB`
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <button onClick={handleCopy} className="text-muted-foreground hover:text-foreground transition-colors">
      {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

// --------------- collapsible JSON section ---------------

function JsonSection({ title, content, defaultOpen = true }: { title: string; content: string | null; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen)
  const [wrap, setWrap] = useState(false)
  const [mode, setMode] = useState<'pretty' | 'raw'>('pretty')

  const pretty = React.useMemo(() => tryFormatJson(content, true), [content])
  const raw = React.useMemo(() => tryFormatJson(content, false), [content])
  const displayed = mode === 'pretty' ? pretty : raw

  if (!content) return null

  const size = new Blob([content]).size
  const lineCount = displayed ? displayed.split('\n').length : 0

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b">
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-1.5 text-sm font-medium hover:text-foreground transition-colors"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {title}
        </button>
        <span className="text-[10px] text-muted-foreground font-mono">
          {formatBytes(size)} · {lineCount} line{lineCount === 1 ? '' : 's'}
        </span>
        {open && (
          <div className="ml-auto flex items-center gap-1">
            <div className="flex rounded-md border bg-background overflow-hidden">
              <button
                onClick={() => setMode('pretty')}
                className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                  mode === 'pretty'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Pretty
              </button>
              <button
                onClick={() => setMode('raw')}
                className={`px-2 py-0.5 text-[10px] font-medium transition-colors ${
                  mode === 'raw'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Raw
              </button>
            </div>
            <button
              onClick={() => setWrap(!wrap)}
              className={`px-2 py-0.5 text-[10px] font-medium rounded border transition-colors ${
                wrap
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground hover:text-foreground'
              }`}
              title="Toggle line wrap"
            >
              Wrap
            </button>
            <CopyButton text={displayed} />
          </div>
        )}
      </div>
      {open && (
        <pre
          className={`p-3 text-xs leading-relaxed font-mono overflow-x-auto bg-muted/10 max-h-[500px] overflow-y-auto ${
            wrap ? 'whitespace-pre-wrap break-words' : 'whitespace-pre'
          }`}
        >
          <code>{highlightJson(displayed)}</code>
        </pre>
      )}
    </div>
  )
}

// --------------- detail panel ---------------

function LogDetailPanel({
  logId,
  open,
  onClose,
}: {
  logId: string | null
  open: boolean
  onClose: () => void
}) {
  const [detail, setDetail] = useState<ApiLogDetail | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!logId || !open) {
      setDetail(null)
      return
    }
    let cancelled = false
    setLoading(true)
    getApiLogDetail(logId)
      .then((d) => { if (!cancelled) setDetail(d) })
      .catch(() => { if (!cancelled) setDetail(null) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [logId, open])

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0">
        {loading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <div className="space-y-3 mt-6">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        ) : !detail ? (
          <div className="p-6 text-center text-muted-foreground">
            <p>Failed to load log details</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <SheetHeader className="p-6 pb-4 border-b">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={METHOD_COLORS[detail.method] ?? 'default'}>{detail.method}</Badge>
                <code className="text-sm font-mono">{detail.path}</code>
                <StatusBadge code={detail.status_code} />
              </div>
              <SheetTitle className="sr-only">
                {detail.method} {detail.path}
              </SheetTitle>
              <SheetDescription className="sr-only">
                API request log detail for {detail.method} {detail.path}
              </SheetDescription>
            </SheetHeader>

            {/* Metadata */}
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Request ID</p>
                  <div className="flex items-center gap-1.5">
                    <code className="text-xs font-mono truncate">{detail.id}</code>
                    <CopyButton text={detail.id} />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Timestamp</p>
                  <p className="text-xs font-mono">{formatFullTimestamp(detail.timestamp)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Latency</p>
                  <p className="font-medium">{formatTime(detail.response_time_ms)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">Status</p>
                  <p className="font-medium">
                    {detail.status_code} {STATUS_LABELS[detail.status_code] || (detail.status_code < 300 ? 'OK' : '')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">IP Address</p>
                  <p className="font-mono text-xs">{detail.ip_address || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-0.5">API Key</p>
                  <p className="truncate">{detail.api_key_label || '-'}</p>
                </div>
                {detail.chatbot_id && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-0.5">Chatbot ID</p>
                    <div className="flex items-center gap-1.5">
                      <code className="text-xs font-mono">{detail.chatbot_id}</code>
                      <CopyButton text={detail.chatbot_id} />
                    </div>
                  </div>
                )}
                {detail.user_agent && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-0.5">User Agent</p>
                    <p className="text-xs text-muted-foreground break-all">{detail.user_agent}</p>
                  </div>
                )}
                {detail.query_params && (
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-0.5">Query Params</p>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded break-all">{detail.query_params}</code>
                  </div>
                )}
              </div>

              {/* Request Body */}
              <JsonSection title="Request Body" content={detail.request_body} />

              {/* Response Body */}
              <JsonSection title="Response Body" content={detail.response_body} />

              {/* Error Detail */}
              {detail.error_detail && detail.status_code >= 400 && (() => {
                const parsed = parseErrorDetail(detail.error_detail)
                const statusLabel = STATUS_LABELS[detail.status_code]
                return (
                  <div className="rounded-md border border-red-200 dark:border-red-900/50 overflow-hidden">
                    <div className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-950/50 border-b border-red-200 dark:border-red-900/50">
                      <XCircle className="h-4 w-4 text-red-500 shrink-0" />
                      <span className="font-semibold text-red-700 dark:text-red-300 text-sm">
                        {detail.status_code} {statusLabel || 'Error'}
                      </span>
                    </div>
                    <div className="px-3 py-2.5 bg-red-50/50 dark:bg-red-950/30 space-y-2">
                      <p className="text-sm text-red-800 dark:text-red-200">{parsed.message}</p>
                      {parsed.details && parsed.details.length > 0 && (
                        <div className="mt-1.5 space-y-1">
                          <p className="text-xs font-medium text-red-700 dark:text-red-300">Validation errors:</p>
                          <ul className="list-disc list-inside text-xs text-red-700 dark:text-red-300 space-y-0.5">
                            {parsed.details.map((d, i) => <li key={i}>{d}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )
              })()}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

// --------------- stats cards ---------------

function StatsCards({ stats, loading }: { stats: ApiLogStats | null; loading: boolean }) {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-5">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const successRate = stats.total_requests > 0
    ? ((stats.success_count / stats.total_requests) * 100).toFixed(1)
    : '0'

  const cards = [
    { title: 'Total Requests', value: stats.total_requests.toLocaleString(), icon: Activity, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950' },
    { title: 'Success Rate', value: `${successRate}%`, icon: CheckCircle2, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950' },
    { title: 'Avg Response Time', value: formatTime(stats.avg_response_time_ms), icon: Clock, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950' },
    { title: 'Errors', value: stats.error_count.toLocaleString(), icon: XCircle, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950' },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((c) => (
        <Card key={c.title}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{c.title}</p>
                <p className="text-2xl font-bold mt-1">{c.value}</p>
              </div>
              <div className={`p-2.5 rounded-lg ${c.bg}`}>
                <c.icon className={`h-5 w-5 ${c.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

// --------------- main page ---------------

export default function ApiLogs() {
  const [logs, setLogs] = useState<ApiLogEntry[]>([])
  const [stats, setStats] = useState<ApiLogStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [statsLoading, setStatsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [refreshing, setRefreshing] = useState(false)

  // Detail panel
  const [selectedLogId, setSelectedLogId] = useState<string | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)

  // Filters
  const [method, setMethod] = useState<string>('')
  const [status, setStatus] = useState<string>('')
  const [pathSearch, setPathSearch] = useState('')
  const [apiKeyLabel, setApiKeyLabel] = useState<string>('')
  const [chatbotId, setChatbotId] = useState<string>('')
  const [period, setPeriod] = useState('24h')

  // Unique filter options derived from all loaded logs
  const [knownApiKeys, setKnownApiKeys] = useState<string[]>([])
  const [knownBotIds, setKnownBotIds] = useState<string[]>([])

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true)
      const filters: ApiLogFilters = { page, limit: 50 }
      if (method) filters.method = method
      if (status) filters.status = status
      if (pathSearch) filters.path = pathSearch
      if (apiKeyLabel) filters.api_key_label = apiKeyLabel
      if (chatbotId) filters.chatbot_id = chatbotId
      const res = await getApiLogs(filters)
      setLogs(res.logs)
      setTotalPages(res.pages)
      setTotal(res.total)

      // Collect unique filter values from results
      const keys = new Set(knownApiKeys)
      const bots = new Set(knownBotIds)
      res.logs.forEach((l) => {
        if (l.api_key_label) keys.add(l.api_key_label)
        if (l.chatbot_id) bots.add(l.chatbot_id)
      })
      setKnownApiKeys(Array.from(keys).sort())
      setKnownBotIds(Array.from(bots).sort())
    } catch {
      // silently fail — empty state shown
    } finally {
      setLoading(false)
    }
  }, [page, method, status, pathSearch, apiKeyLabel, chatbotId])

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true)
      const s = await getApiLogStats(period)
      setStats(s)
    } catch {
      // silently fail
    } finally {
      setStatsLoading(false)
    }
  }, [period])

  useEffect(() => { fetchLogs() }, [fetchLogs])
  useEffect(() => { fetchStats() }, [fetchStats])

  const handleRefresh = async () => {
    setRefreshing(true)
    await Promise.all([fetchLogs(), fetchStats()])
    setRefreshing(false)
  }

  const handleExport = async () => {
    const filters: ApiLogFilters = {}
    if (method) filters.method = method
    if (status) filters.status = status
    if (pathSearch) filters.path = pathSearch
    if (apiKeyLabel) filters.api_key_label = apiKeyLabel
    if (chatbotId) filters.chatbot_id = chatbotId
    await exportApiLogsCsv(filters)
  }

  const handleRowClick = (logId: string) => {
    setSelectedLogId(logId)
    setPanelOpen(true)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">API Request Logs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Monitor your API usage, response times, and errors
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SelectRoot value={period} onValueChange={setPeriod}>
            <SelectTrigger size="sm" className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">24 hours</SelectItem>
              <SelectItem value="7d">7 days</SelectItem>
              <SelectItem value="30d">30 days</SelectItem>
            </SelectContent>
          </SelectRoot>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-4 w-4 mr-1.5" /> Export
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <StatsCards stats={stats} loading={statsLoading} />

      {/* Filters + Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-base">Request Log</CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Filter by path..."
                  value={pathSearch}
                  onChange={(e) => { setPathSearch(e.target.value); setPage(1) }}
                  className="pl-8 h-8 w-[180px] text-sm"
                />
              </div>
              <SelectRoot value={method} onValueChange={(v) => { setMethod(v === 'all' ? '' : v); setPage(1) }}>
                <SelectTrigger size="sm" className="w-[100px]">
                  <SelectValue placeholder="Method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                  <SelectItem value="PUT">PUT</SelectItem>
                  <SelectItem value="PATCH">PATCH</SelectItem>
                  <SelectItem value="DELETE">DELETE</SelectItem>
                </SelectContent>
              </SelectRoot>
              <SelectRoot value={status} onValueChange={(v) => { setStatus(v === 'all' ? '' : v); setPage(1) }}>
                <SelectTrigger size="sm" className="w-[100px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </SelectRoot>
              {knownApiKeys.length > 0 && (
                <SelectRoot value={apiKeyLabel} onValueChange={(v) => { setApiKeyLabel(v === 'all' ? '' : v); setPage(1) }}>
                  <SelectTrigger size="sm" className="w-[130px]">
                    <SelectValue placeholder="API Key" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Keys</SelectItem>
                    {knownApiKeys.map((k) => (
                      <SelectItem key={k} value={k}>{k}</SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              )}
              {knownBotIds.length > 0 && (
                <SelectRoot value={chatbotId} onValueChange={(v) => { setChatbotId(v === 'all' ? '' : v); setPage(1) }}>
                  <SelectTrigger size="sm" className="w-[130px]">
                    <SelectValue placeholder="Bot ID" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Bots</SelectItem>
                    {knownBotIds.map((b) => (
                      <SelectItem key={b} value={b}>{b.slice(0, 8)}...</SelectItem>
                    ))}
                  </SelectContent>
                </SelectRoot>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/40">
                  <th className="p-3 text-left font-medium text-muted-foreground">Timestamp</th>
                  <th className="p-3 text-left font-medium text-muted-foreground">Method</th>
                  <th className="p-3 text-left font-medium text-muted-foreground">Path</th>
                  <th className="p-3 text-left font-medium text-muted-foreground">Status</th>
                  <th className="p-3 text-right font-medium text-muted-foreground">Latency</th>
                  <th className="p-3 text-left font-medium text-muted-foreground">API Key</th>
                  <th className="p-3 text-left font-medium text-muted-foreground">IP</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => (
                    <tr key={i} className="border-b">
                      {Array.from({ length: 7 }).map((_, j) => (
                        <td key={j} className="p-3"><Skeleton className="h-4 w-full" /></td>
                      ))}
                    </tr>
                  ))
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-12 text-center text-muted-foreground">
                      <Activity className="h-10 w-10 mx-auto mb-3 opacity-30" />
                      <p className="font-medium">No API requests found</p>
                      <p className="text-xs mt-1">Requests to your /v1/ API endpoints will appear here</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b transition-colors hover:bg-muted/50 cursor-pointer"
                      onClick={() => handleRowClick(log.id)}
                    >
                      <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td className="p-3">
                        <Badge variant={METHOD_COLORS[log.method] ?? 'default'} size="sm">{log.method}</Badge>
                      </td>
                      <td className="p-3 text-sm font-mono truncate max-w-[300px]">{log.path}</td>
                      <td className="p-3"><StatusBadge code={log.status_code} /></td>
                      <td className="p-3 text-sm text-right tabular-nums">{formatTime(log.response_time_ms)}</td>
                      <td className="p-3 text-sm text-muted-foreground truncate max-w-[120px]">{log.api_key_label || '-'}</td>
                      <td className="p-3 text-sm text-muted-foreground font-mono truncate max-w-[120px]">{log.ip_address || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <p className="text-xs text-muted-foreground">
                {total.toLocaleString()} total results
              </p>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="text-xs text-muted-foreground px-2">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail slide-over panel */}
      <LogDetailPanel
        logId={selectedLogId}
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
      />
    </div>
  )
}
